export type ManualRefundStatus = 'RECORDED' | 'PENDING_RECONCILIATION' | 'RECONCILED' | 'REJECTED';

export interface ManualRefund {
  readonly id: string;
  readonly amount: number;
  readonly currency: string;
  readonly status: ManualRefundStatus;
  readonly externalReference: string;
  readonly note: string;
  readonly actor: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly rejectedReason?: string | null;
}

export interface BookingRefunds {
  readonly bookingId: string;
  readonly currency: string;
  readonly totalPaid: number;
  readonly totalReserved: number;
  readonly remainingBalance: number | null;
  readonly canRecord: boolean;
  readonly requiresLegacyReconciliation: boolean;
  readonly blockingReason: string | null;
  readonly refunds: ManualRefund[];
}

export interface ManualRefundCreate {
  readonly amount: number;
  readonly externalReference: string;
  readonly note: string;
}

export type ManualRefundTransition = Exclude<ManualRefundStatus, 'RECORDED'>;

const TRANSITIONS: Record<ManualRefundStatus, readonly ManualRefundTransition[]> = {
  RECORDED: ['PENDING_RECONCILIATION'],
  PENDING_RECONCILIATION: ['RECONCILED', 'REJECTED'],
  RECONCILED: [],
  REJECTED: [],
};

/**
 * Valid next states from a refund's current state, per the canonical chain
 * RECORDED → PENDING_RECONCILIATION → RECONCILED | REJECTED. Surfaces render
 * only these, so an invalid transition can never be offered.
 */
export function refundTransitionsFrom(
  status: ManualRefundStatus
): readonly ManualRefundTransition[] {
  return TRANSITIONS[status] ?? [];
}

/**
 * Whether a refund in this state reserves or consumes refundable balance.
 * Only a rejected refund frees its amount back into the remaining balance.
 */
export function refundConsumesBalance(status: ManualRefundStatus): boolean {
  return status !== 'REJECTED';
}
